import { createFileRoute, Link } from "@tanstack/react-router";
import { Section, SectionHeader } from "@/components/Section";
import { whatsappUrl } from "@/lib/site";

export const Route = createFileRoute("/academia")({
  head: () => ({
    meta: [
      { title: "Academia SVG — Domine o Google Meu Negócio | SVG Multimídia" },
      {
        name: "description",
        content:
          "Plataforma educacional da SVG Multimídia. Aprenda a otimizar sua Ficha do Google, dominar SEO local e transformar buscas em clientes reais.",
      },
      { property: "og:title", content: "Academia SVG — Aprenda SEO Local e Google Meu Negócio" },
      {
        property: "og:description",
        content:
          "Trilha completa de Google Meu Negócio, SEO local e presença digital. Aulas, tarefas práticas, gamificação e certificados.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: AcademiaLanding,
});

const MODULES = [
  { n: "01", title: "Fundamentos do Google Meu Negócio", desc: "Como funciona o algoritmo local do Google e por que a Ficha é sua vitrine mais valiosa." },
  { n: "02", title: "Criando e verificando sua Ficha", desc: "Passo a passo para criar, reivindicar e verificar sua Ficha sem erros." },
  { n: "03", title: "Categorias e palavras-chave locais", desc: "Escolha da categoria principal, secundárias e o mapeamento correto para sua cidade." },
  { n: "04", title: "Fotos que convertem", desc: "Padrão SVG de fotos, capa, logo, produtos, equipe e ambientes que aumentam ligações." },
  { n: "05", title: "Postagens semanais estratégicas", desc: "Cronograma editorial de posts que mantêm o algoritmo ativo." },
  { n: "06", title: "Avaliações e resposta automática", desc: "Estratégia para receber avaliações reais e responder de forma que gere ranqueamento." },
  { n: "07", title: "Perguntas & Respostas (Q&A)", desc: "Como transformar dúvidas em conteúdo SEO na sua Ficha." },
  { n: "08", title: "Produtos e Serviços", desc: "Cadastro correto para aparecer em buscas específicas." },
  { n: "09", title: "Mensagens diretas pelo Google", desc: "Ativação, respostas rápidas e integração com WhatsApp." },
  { n: "10", title: "Insights e métricas", desc: "Como ler os dados do painel para tomar decisões de crescimento." },
  { n: "11", title: "Google Ads Local (LSA)", desc: "Anúncios de Serviços Locais para quem quer acelerar resultados." },
  { n: "12", title: "SEO local avançado", desc: "Citations, NAP, backlinks locais e como escalar a autoridade." },
  { n: "13", title: "Multi-unidade e franquias", desc: "Gestão de várias fichas, permissões e padronização de marca." },
  { n: "14", title: "Reputação e crises", desc: "Como agir diante de avaliações negativas e ataques de concorrentes." },
  { n: "15", title: "Combo Autoridade Local", desc: "Como integrar Ficha + notícias em portais nacionais para dominar sua cidade." },
];

function AcademiaLanding() {
  return (
    <main>
      {/* Hero */}
      <Section className="pt-24 md:pt-32">
        <div className="mx-auto max-w-4xl text-center">
          <span className="eyebrow">Academia SVG</span>
          <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-6xl">
            Domine o <span className="text-gradient">Google Meu Negócio</span> e transforme buscas em clientes reais
          </h1>
          <p className="mt-6 text-lg text-muted-foreground md:text-xl">
            15 módulos práticos, tarefas revisadas, sistema de pontos, badges e certificado.
            A mesma metodologia que a SVG usa em centenas de clientes — agora disponível para
            empresários que querem chegar ao topo do Google.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href={whatsappUrl("Olá! Quero contratar acesso à Academia SVG.")}
              target="_blank"
              rel="noopener"
              className="btn-primary"
            >
              Contratar acesso
            </a>
            <Link to="/criar-conta" className="btn-outline">
              Já contratei · Criar conta
            </Link>
            <Link to="/entrar" className="btn-outline">Entrar</Link>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Acesso liberado após aprovação da conta pela equipe SVG.
          </p>
        </div>
      </Section>

      {/* Benefits */}
      <Section className="pt-0">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { t: "Aulas práticas", d: "Vídeo + resumo + tarefa. Cada aula tem entrega para você aplicar no seu negócio." },
            { t: "Gamificação", d: "Ganhe pontos, suba de nível e conquiste badges à medida que avança nos módulos." },
            { t: "Certificado SVG", d: "Ao concluir a trilha, você recebe um certificado digital com código verificável." },
          ].map((b) => (
            <div key={b.t} className="card-glass">
              <h3 className="text-lg font-semibold">{b.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{b.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Trilha */}
      <Section>
        <SectionHeader
          eyebrow="Trilha completa"
          title="15 módulos que cobrem tudo sobre presença local no Google"
          description="Do zero absoluto até estratégias avançadas de autoridade local — no seu ritmo, com suporte no WhatsApp."
          center
        />
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {MODULES.map((m) => (
            <div key={m.n} className="card-glass">
              <div className="text-xs font-mono text-primary/80">MÓDULO {m.n}</div>
              <h3 className="mt-1 text-base font-semibold">{m.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{m.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* CTA final */}
      <Section>
        <div className="card-glass text-center">
          <h2 className="text-3xl font-bold md:text-4xl">Pronto para chegar ao topo do Google?</h2>
          <p className="mt-3 text-muted-foreground">
            Contrate seu acesso, crie sua conta e comece assim que aprovarmos seu cadastro (normalmente em poucas horas).
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a
              href={whatsappUrl("Olá! Quero contratar acesso à Academia SVG.")}
              target="_blank"
              rel="noopener"
              className="btn-primary"
            >
              Contratar acesso
            </a>
            <Link to="/criar-conta" className="btn-outline">
              Já contratei · Criar conta
            </Link>
            <Link to="/entrar" className="btn-outline">Entrar</Link>
          </div>
        </div>
      </Section>
    </main>
  );
}